import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../mock';

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play: Bewegt sich alle 5 Sekunden automatisch
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Unique diagonal background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#141414] to-[#00CCDD]"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-white transform skew-x-12 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-full bg-white transform -skew-x-12 -translate-x-1/4"></div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full mb-6">
            <Star className="w-4 h-4 fill-current" />
            <span className="font-semibold text-sm uppercase tracking-wide">Kundenstimmen</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Was unsere Kunden sagen
          </h2>
          <p className="text-xl text-white/90">
            Zufriedene Kunden sind unser größter Erfolg
          </p>
        </div>

        {/* Testimonial Slider */}
        <div className="relative max-w-5xl mx-auto mb-16">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="w-full flex-shrink-0 px-4"
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 sm:p-12 shadow-2xl">
                    {/* Stars */}
                    <div className="flex justify-center gap-1 mb-6">
                      {[...Array(testimonial.rating)].map((_, idx) => (
                        <Star key={`star-${testimonial.id}-${idx}`} className="w-6 h-6 fill-[#FFD700] text-[#FFD700]" />
                      ))}
                    </div>
                    
                    {/* Text */}
                    <p className="text-gray-700 mb-8 leading-relaxed text-xl sm:text-2xl italic text-center max-w-3xl mx-auto">
                      "{testimonial.text}"
                    </p>
                    
                    {/* Author */}
                    <div className="text-center pt-6 border-t border-gray-200">
                      <p className="font-bold text-black text-xl">{testimonial.name}</p>
                      <p className="text-gray-600">{testimonial.location}</p>
                      <div className="mt-3 inline-block bg-gradient-to-r from-[#E6F7FF] to-[#CCF2FF] text-black text-sm font-semibold px-4 py-2 rounded-full">
                        {testimonial.project}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border-2 border-white/30 flex items-center justify-center hover:bg-[#00CCDD] hover:border-[#00CCDD] transition-all duration-300 group"
            aria-label="Vorherige Bewertung"
          >
            <ChevronLeft className="w-6 h-6 text-white group-hover:text-black" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border-2 border-white/30 flex items-center justify-center hover:bg-[#00CCDD] hover:border-[#00CCDD] transition-all duration-300 group"
            aria-label="Nächste Bewertung"
          >
            <ChevronRight className="w-6 h-6 text-white group-hover:text-black" />
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-3 mb-16">
          {testimonials.map((testimonial, index) => (
            <button
              key={`dot-${testimonial.id}`}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-12 h-3 bg-[#00CCDD]'
                  : 'w-3 h-3 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Bewertung ${index + 1}`}
            />
          ))}
        </div>

        {/* Trust Metrics */}
        <div className="relative">
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 sm:p-12 border border-white/20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 divide-y sm:divide-y-0 sm:divide-x divide-white/20">
              <div className="text-center pt-6 sm:pt-0">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, index) => (
                      <Star key={`trust-star-${index}`} className="w-8 h-8 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                </div>
                <p className="text-5xl font-bold text-white mb-2">5.0</p>
                <p className="text-white/80 font-medium">Durchschnittliche Bewertung</p>
              </div>
              
              <div className="text-center pt-6 sm:pt-0">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#00CCDD] to-white/30 flex items-center justify-center">
                  <span className="text-3xl">👥</span>
                </div>
                <p className="text-5xl font-bold text-white mb-2">150+</p>
                <p className="text-white/80 font-medium">Zufriedene Kunden</p>
              </div>
              
              <div className="text-center pt-6 sm:pt-0">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-white/30 to-[#00CCDD] flex items-center justify-center">
                  <span className="text-3xl">🏆</span>
                </div>
                <p className="text-5xl font-bold text-white mb-2">98%</p>
                <p className="text-white/80 font-medium">Weiterempfehlungsrate</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};